<script>
import { required, email } from "@vuelidate/validators";
import { useForms } from "@/components/organisms/forms/mixin";
import { tel, zipCode, birthdate } from "@/utils/form-validators";
import FormInput from "@/components/molecules/form-input";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormError from "@/components/molecules/form/form-error";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import FormFieldset from "@/components/molecules/form-fieldset";
import ButtonGroup from "@/components/molecules/button-group";

export default {
  name: "Newsletter",
  components: {
    FormFieldset,
    ButtonGroup,
    Captcha,
    FormInput,
    Button,
    Form,
    FormGroup,
    FormSelectOptGroup,
    FormError,
    FormSelect,
    FormSelectOption,
    SuccessPopin
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false
    },
    magasinsByCp: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { redirectToPreviousPage } = useForms();
    const {
      captcha,
      v$,
      getState,
      getErrorMessage,
      scrollToFirstError,
      checkValidation,
      states,
      isError,
      isSuccess,
      isLoadingComputed,
      errorsArray
    } = useFormValidation(props);
    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      model: {
        id_magasin: this.magasinId ? this.magasinId : "",
        email: this.$route.query.email || "",
        nom: "",
        prenom: "",
        date_naissance: "",
        adresse: "",
        ville: "",
        code_postal: "",
        tel: ""
      },
      captchaToken: null,
      finalError: null
    };
  },
  computed: {
    otherMagasins() {
      const magasins = this.magasins.filter((magasin) => {
        return !this.magasinsByCp.some((mag) => mag.id === magasin.id);
      });
      if (!this.magasinId) {
        return magasins;
      }
      return magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    }
  },
  validations() {
    return {
      model: {
        id_magasin: {
          required
        },
        email: {
          required,
          email
        },
        date_naissance: {
          birthdate
        },
        code_postal: {
          zipCode
        },
        tel: {
          tel
        }
      }
    };
  },
  methods: {
    send() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    },
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.model,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    }
  }
};
</script>

<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <br>
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <FormError
      v-if="error"
      :error="error"
    />
    <br>
    <h2 class="u-font-19 u-mb-xs">
      Sélectionnez le magasin Cora près de chez vous !*
    </h2>

    <Form
      class="c-inscription-form u-mx-xxlg u-mx-lg@md"
      @submit.prevent="send()"
    >
      <FormFieldset
        center
        size="full"
      >
        <FormGroup
          :state="getState(v$,'id_magasin', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormSelect
            v-model="model.id_magasin"
            title="Magasin"
            :state="getState(v$,'id_magasin', 'model')"
            :default-value="'Sélectionnez un magasin'"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup
              v-if="magasinsByCp.length > 0"
              label="Les Cora proche de chez vous"
            >
              <FormSelectOption
                v-for="magasin in magasinsByCp"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins Cora">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <p>
              <template v-if="v$.model.id_magasin.required.$invalid">
                Merci de choisir un magasin
              </template>
              <template v-else>
                {{ getErrorMessage("id_magasin") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'email','model')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="email"
            v-model.trim="model.email"
            :state="getState(v$,'email', 'model')"
            type="email"
            autocapitalize="off"
            size="full"
            @blur="v$.model.email.$touch()"
            @input="v$.model.email.$reset()"
          >
            Email *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.email.required.$invalid">
                Merci de renseigner votre adresse e-mail
              </template>
              <template v-if="v$.model.email.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("email") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'nom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nom"
            v-model.trim="model.nom"
            :state="getState(v$,'nom', 'model')"
            size="full"
          >
            Nom
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("nom") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'prenom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="prenom"
            v-model.trim="model.prenom"
            :state="getState(v$,'prenom', 'model')"
            maxlength="40"
            size="full"
          >
            Prénom
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("prenom") }}
            </p>
          </template>
        </FormGroup>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance
        </label>
        <FormGroup
          :state="getState(v$,'date_naissance','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date"
            v-model.trim="model.date_naissance"
            type="date"
            :state="getState(v$,'date_naissance','model')"
            size="full"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'adresse', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="adresse"
            v-model.trim="model.adresse"
            :state="getState(v$,'adresse', 'model')"
            maxlength="255"
            size="full"
          >
            Adresse
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("adresse") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'ville', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="ville"
            v-model.trim="model.ville"
            :state="getState(v$,'ville', 'model')"
            size="full"
          >
            Ville
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("ville") }}
            </p>
          </template>
        </Formgroup>

        <FormGroup
          :state="getState(v$,'code_postal', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="cp"
            v-model="model.code_postal"
            :state="getState(v$,'code_postal', 'model')"
            type="tel"
            size="full"
            @blur="v$.model.code_postal.$touch()"
            @input="v$.model.code_postal.$reset()"
          >
            Code Postal
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.code_postal.zipCode.$invalid">
                merci de saisir un code postal valide
              </template>
              <template v-else>
                {{ getErrorMessage("code_postal") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'tel', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="tel"
            v-model.trim="model.tel"
            :state="getState(v$,'tel', 'model')"
            size="full"
            @blur="v$.model.tel.$touch()"
            @input="v$.model.tel.$reset()"
          >
            Téléphone
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.tel.tel.$invalid">
                Ce numéro de téléphone est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("tel") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <Captcha
          ref="captcha"
          @verify="verifyCaptcha"
        />
        <ButtonGroup center>
          <Button
            variant="secondary"
            radius="full"
            type="submit"
            size="md"
            class="u-mt-sm u-flex-justify-center@sm "
            value="M'inscrire"
            :disabled="state ===states.LOADING"
          />
        </ButtonGroup>
      </FormFieldset>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
